// react
import React, { useEffect, useRef } from "react";
// redux
import { useDispatch, useSelector } from "react-redux";
import { GenerateProcess } from "redux-process";

import { AdminContainer } from "../../../components/Admin";
import BeforeUnload from "../../../components/BeforeUnload";
import Breadcrumbs from "../../../components/Breadcrumbs";
// components
import Button from "../../../components/Button";
import PageTitle from "../../../components/PageTitle";
import Spacer from "../../../components/Spacer";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import AddBackupField from "./components/AddBackupField";
import AddIntegrationEmails from "./components/AddIntegrationEmails";
import AddIntegrationName from "./components/AddIntegrationName";
import FieldMapping from "./components/FieldMapping";
import ToggleDeactivate from "./components/ToggleDeactivate";
import AddTestingMode from "./components/ToggleTestingMode";

import ErrorPage from "../../../pages/Error";

import useReportStore from "./store";

import { useCompanyFunction } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
// hooks
import useRequest from "../../../hooks/useRequest";

import { ChangePage } from "../../../actions/navigation.actions";
// actions
import { DisplayError, DisplaySuccess } from "../../../actions/notifications.actions";

// dependencies
// selectors
import { getUserCompanyID, getUserCompanyName } from "../../../selectors/app";
import { getURLParam } from "../../../selectors/navigation";

// utils
// styles
import css from "./Sftp.module.scss";

const COMPANY_FIELDS_PAGE_SIZE = 50;

const SftpSettings = () => {
	const idFromURL = useSelector((state) => getURLParam(state, "integrationID"));

	const [
		init,
		sftpIntegrationID,
		sftpIntegrationName,
		isTestingMode,
		deactivateUsersOnMissing,
		integrationEmailList,
		backupIDField,
		fieldMapOverrides,
		setSftpIntegrationName,
		setIsTestingMode,
		setDeactivateUsersOnMissing,
		removeIntegrationEmail,
		addIntegrationEmail,
		setIntegrationEmailList,
		updateIntegartionEmail,
		setBackupIDField,
		addFieldMapOverride,
		setFieldMapOverrides,
		removeFieldMapOverride,
		updateFieldMapOverride,
		companyCustomFields,
		setCompanyCustomFields,
		customFieldsAvailableForFieldMap,
		checkForChanges,
	] = useReportStore((state) => [
		state.init,
		state.sftpIntegrationID,
		state.sftpIntegrationName,
		state.isTestingMode,
		state.deactivateUsersOnMissing,
		state.integrationEmailList,
		state.backupIDField,
		state.fieldMapOverrides,
		state.setSftpIntegrationName,
		state.setIsTestingMode,
		state.setDeactivateUsersOnMissing,
		state.removeIntegrationEmail,
		state.addIntegrationEmail,
		state.setIntegrationEmailList,
		state.updateIntegartionEmail,
		state.setBackupIDField,
		state.addFieldMapOverride,
		state.setFieldMapOverrides,
		state.removeFieldMapOverride,
		state.updateFieldMapOverride,
		state.companyCustomFields,
		state.setCompanyCustomFields,
		state.customFieldsAvailableForFieldMap,
		state.checkForChanges,
	]);

	const dispatch = useDispatch();

	const companyID = useSelector((state) => getUserCompanyID(state));
	const company = useSelector((state) => getUserCompanyName(state));

	// Function and feature flag queries for SFTP integration
	const { companyFunction: sftpIntegrationFunctionState, isLoading: isLoadingCompanyFunction } =
		useCompanyFunction("integrationSftp");
	const sftpIntegrationFunctionEnabled = sftpIntegrationFunctionState === "enabled";
	const {
		value: ffSftpIntegrationEnabled,
		query: { isLoading: isLoadingFeatureFlag },
	} = useFeatureFlagQuery("ff-is-enabled-sftp-integration", false);

	const { data: sftpIntegrationsData, revalidate: revalidateSftpIntegrationData } = useRequest(
		"getSftpIntegration",
		{ filters: { integrationID: idFromURL } },
		{ skipCache: true, manual: false }
	);

	const { revalidate: addSftpIntegration } = useRequest("addSftpIntegration", {}, { skipCache: true, manual: true });

	const { revalidate: editSftpIntegration } = useRequest("editSftpIntegration", {}, { manual: true });

	const isSaving = useRef(false);

	useEffect(() => {
		if (idFromURL) {
			if (sftpIntegrationsData && sftpIntegrationsData.results) {
				init(sftpIntegrationsData.results[0]);
			}
		} else {
			init({});
		}
	}, [sftpIntegrationsData, idFromURL]);

	useEffect(() => {
		let cancelled = false;

		// The fields endpoint returns at most 50 fields per request, so page
		// through it until a partial page comes back.
		const fetchAllCompanyFields = async () => {
			const allFields = [];
			let offset = 0;
			let hasMoreFields = true;

			while (hasMoreFields) {
				const result = await dispatch(GenerateProcess("GetCompanyFields", { companyID, offset }));

				if (!result?.succeeded || !result?.response) {
					if (!cancelled) {
						dispatch(DisplayError("Unable to load custom fields. Refresh the page to try again."));
					}
					return;
				}

				allFields.push(...result.response.toJS());
				hasMoreFields = result.response.size === COMPANY_FIELDS_PAGE_SIZE;
				offset += COMPANY_FIELDS_PAGE_SIZE;
			}

			if (!cancelled) {
				setCompanyCustomFields(allFields);
			}
		};

		if (companyID) {
			fetchAllCompanyFields();
		}

		return () => {
			cancelled = true;
		};
	}, [companyID]);

	const trimFieldOverrides = (fieldOverrides) => {
		return fieldOverrides.map((override) => {
			if (override.csv_column_name) {
				return {
					...override,
					csv_column_name: override.csv_column_name.trim(),
				};
			} else {
				return override;
			}
		});
	};

	const removeEmptyFieldOverrides = (fieldOverrides) =>
		fieldOverrides.filter(
			(fieldOverride) => fieldOverride.csv_column_name && fieldOverride.performyard_custom_field_name
		);

	const removeEmptyIntegrationEmails = (integrationEmails) =>
		integrationEmails.filter((integrationEmail) => !!integrationEmail);

	const handleSubmit = async () => {
		// when attempting to save, set isSaving to true and remove the beforeunload listener.
		isSaving.current = true;
		// on save remove leading and trailing whitespace on field override CSV column names.
		const trimmedFieldMapOverrides = trimFieldOverrides(fieldMapOverrides);
		setFieldMapOverrides(trimmedFieldMapOverrides);

		// on save remove empty field overrides.
		const nonEmptyFieldOverrides = removeEmptyFieldOverrides(trimmedFieldMapOverrides);
		// on save remove empty integration emails.
		const nonEmptyIntegrationEmails = removeEmptyIntegrationEmails(integrationEmailList);
		setIntegrationEmailList(nonEmptyIntegrationEmails);

		const payload = {
			name: sftpIntegrationName,
			testingModeEnabled: isTestingMode,
			deactivateUsersOnMissing,
			emails: nonEmptyIntegrationEmails,
			backupIDField,
			fieldMappingList: nonEmptyFieldOverrides,
		};

		if (idFromURL) {
			const resp = await editSftpIntegration({
				filters: {
					payload: payload,
					integrationID: sftpIntegrationID,
				},
			});

			await revalidateSftpIntegrationData();

			if (resp && resp.data && resp.data.errors.length === 0) {
				dispatch(DisplaySuccess("Integration updated successfully"));
			} else if (resp && resp.data && resp.data.errors.length > 0) {
				resp.data.errors.map((error) => dispatch(DisplayError(error)));
			} else {
				dispatch(DisplayError("Something went wrong"));
			}
		} else {
			const resp = await addSftpIntegration({
				filters: {
					payload: payload,
				},
			});

			await revalidateSftpIntegrationData();

			if (resp && resp.data && resp.data.errors.length === 0) {
				dispatch(DisplaySuccess("Integration created successfully"));

				dispatch(ChangePage("/administration/sftp/"));
			} else if (resp && resp.data && resp.data.errors.length > 0) {
				resp.data.errors.map((error) => dispatch(DisplayError(error)));
			} else {
				dispatch(DisplayError("Something went wrong"));
			}
		}
		isSaving.current = false;
	};

	const selectBreadCrumbLabel = () => {
		if (idFromURL) {
			if (sftpIntegrationName) {
				return sftpIntegrationName;
			}
			return "";
		}

		return "Create SFTP Integration";
	};

	// Show a loading spinner while the feature flag is being fetched
	if (isLoadingCompanyFunction || isLoadingFeatureFlag) {
		return <ComponentLoader />;
	}

	// Only show the error page after we've confirmed the feature flag is disabled
	if (!sftpIntegrationFunctionEnabled || !ffSftpIntegrationEnabled) {
		return <ErrorPage code={403} />;
	}

	return (
		<AdminContainer>
			<div className={css.sftpContainer}>
				<Breadcrumbs
					className="breadcrumbs"
					forceHardReload
					pages={[
						{
							pageID: "sftp",
							label: "SFTP Integrations",
							url: "/administration/sftp/",
						},
						{
							pageID: "sftpAddOrEdit",
							label: selectBreadCrumbLabel(),
							url: "/administration/sftp/create/",
						},
					]}
				/>
				<PageTitle title={`SFTP Setup - ${company} - PerformYard`} />
				<Spacer height={4} />
				<div>
					<div style={{ width: "40%", float: "left" }}>
						<AddIntegrationName
							integrationName={sftpIntegrationName}
							setSftpIntegrationName={setSftpIntegrationName}
						/>
						<Spacer height={2} />
						<AddTestingMode testingMode={isTestingMode} setIsTestingMode={setIsTestingMode} />
						<Spacer height={2} />
						<ToggleDeactivate
							deactivateUsersOnMissing={deactivateUsersOnMissing}
							setDeactivateUsersOnMissing={setDeactivateUsersOnMissing}
						/>
						<Spacer height={4} />
						<AddIntegrationEmails
							integrationEmailList={integrationEmailList}
							addIntegrationEmail={addIntegrationEmail}
							removeIntegrationEmail={removeIntegrationEmail}
							updateIntegartionEmail={updateIntegartionEmail}
						/>
						<Spacer height={4} />
						<div style={{ marginBottom: "32px" }}>
							<AddBackupField
								backupIDField={backupIDField}
								companyCustomFields={companyCustomFields}
								setBackupIDField={setBackupIDField}
							/>
						</div>
						<Spacer height={4} />
						<Button
							version={2}
							color="blue"
							style={{ height: "37px", marginRight: "7px" }}
							onClick={handleSubmit}
						>
							Save
						</Button>
					</div>
					<div style={{ width: "55%", float: "right" }}>
						<FieldMapping
							fieldMapOverrides={fieldMapOverrides}
							addFieldMapOverride={addFieldMapOverride}
							removeFieldMapOverride={removeFieldMapOverride}
							updateFieldMapOverride={updateFieldMapOverride}
							companyCustomFields={customFieldsAvailableForFieldMap}
						/>
					</div>
				</div>
				<BeforeUnload checkForUnsavedData={() => (isSaving.current ? false : checkForChanges())} />
			</div>
		</AdminContainer>
	);
};

export default SftpSettings;
